import {useCallback, useMemo} from 'react';
import {addDoc, collection, deleteDoc, deleteField, doc, query, updateDoc, where} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {Exam, ExamInsertOptions, ExamUpdateOptions} from '../types/Exam';
import {MONGO_DATE_TIME_FORMAT} from "../utils/date";
import {firestore} from "../App";
import moment from "moment/moment";
import {processRemindAt, processSteps} from "./common/event";

interface UseExamManagerProps {
    plannerId: string;
}

export function useExamManager({plannerId}: UseExamManagerProps) {

    const examsQuery = useMemo(() => {
        if (!plannerId) return null;

        return query(
            collection(firestore, 'syncedPlanners', plannerId, 'events'),
            where("eventKind", "==", "exam") // Filter for 'exam'
        );
    }, [plannerId]);

    const [examsSnapshot, loadingExams, errorExams] = useCollection(examsQuery);

    const exams = useMemo(() => {
        return examsSnapshot?.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Exam)) || []; // Cast as Exam
    }, [examsSnapshot]);

    /**
     * Inserts an exam document in the database.
     */
    const createExam = useCallback(async (options: ExamInsertOptions) => {
        if (!plannerId) return;

        const examDoc: any = {
            eventKind: 'exam',
            plannerId: plannerId,
            subjectId: options.subjectId,
            title: options.title,
            date: options.date,
            category: options.category,
            archived: options.isArchived,
            note: options.note,
            ...processRemindAt(options.remindAt),
            ...processSteps(options.steps),
            createdOn: moment().format(MONGO_DATE_TIME_FORMAT)
        };

        // Firestore does not allow 'undefined' values.
        Object.keys(examDoc).forEach(key => {
            if (examDoc[key] === undefined) {
                delete examDoc[key];
            }
        });

        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'events');

        try {
            console.log("Creating Exam: ", examDoc);
            const docRef = await addDoc(colRef, examDoc);
            console.log("Exam created successfully with ID: ", docRef.id);
            return docRef.id;
        } catch (error) {
            console.error("Error creating Exam: ", error);
        }
        await addDoc(colRef, examDoc);

    }, [plannerId]);

    /**
     * Updates an exam document in the database.
     */
    const updateExam = useCallback(async (examId: string, options: ExamUpdateOptions) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'events', examId);

        const updateData: { [key: string]: any } = {
            subjectId: options.subjectId ?? deleteField(),
            title: options.title,
            date: options.date,
            isArchived: options.isArchived,
            category: options.category ?? deleteField(),
            note: options.note ?? deleteField(),
            ...processRemindAt(options.remindAt),
            ...processSteps(options.steps)
        };

        try {
            console.log("Updating Exam: ", updateData);
            await updateDoc(docRef, updateData);
            console.log("Exam created updated with ID: ", docRef.id);
            return docRef.id;
        } catch (error) {
            console.error("Error updating Exam: ", error);
        }
    }, [plannerId]);

    /**
     * Deletes an exam from the database.
     */
    const deleteExam = useCallback(async (examId: string) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'events', examId);
        await deleteDoc(docRef);
    }, [plannerId]);

    return {
        exams,
        loadingExams,
        errorExams,
        createExam,
        updateExam,
        deleteExam
    };
}